import React, { useEffect, useRef, useState } from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Fade, Grow, Modal, Typography } from '@mui/material';
import Button2 from '../../atoms/button2/button2';
import RichtextDescription from '../../atoms/richtext-description/richtextDescription';
import DropdownMenu from '../../molecules/dropdown-menu/dropdownMenu';
import useScreenSize from '../../../hooks/useScreenSize';
import {
    extractUniqueKeyValues,
    groupBy,
    separarPorHR,
} from '../../../utils/arrayUtils';
import ModalForCard from '../../molecules/modal-for-card/modalForCard';
import * as styles from './cardsCategorias.module.scss';
import { CardImgCta } from '@comfama/comfama-ui-react';
import { richTextToPlainText } from '../../../utils/richTextToPlainText';

export interface ICardsCategoriasProps {
    title: string;
    subtitle: any;
    bg: string;
    btnLabel: string;
    btnAction: string;
    cardsList: Array<{
        bg: string;
        title: string;
        description: any;
        label: string;
        category: string;
        redirect: string;
        btnTitle: string;
        isCard: boolean;
        cardLibUI: string | null;
    }>;
}

type SelectedCatType = {
    index: number;
    label: string;
};

const CardsCategorias = ({
    title,
    subtitle,
    bg,
    btnLabel,
    btnAction,
    cardsList,
}: ICardsCategoriasProps) => {
    const [tabs, setTabs] = useState<string[]>([]);
    const [groupedData, setGroupedData] = useState<Record<string, any[]>>({});
    const [isVisible, setIsVisible] = useState(false);
    const screenSize = useScreenSize();
    const [open, setOpen] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [selectedCat, setSelectedCat] = useState<SelectedCatType>({
        index: 0,
        label: cardsList[0].category,
    });
    const [checked, setChecked] = useState(true);
    const cardsCatRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                setIsVisible(entries[0].isIntersecting);
            }
        });

        if (cardsCatRef.current) {
            observer.observe(cardsCatRef.current);
        }

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        setGroupedData(groupBy(cardsList, 'category'));
        setTabs(extractUniqueKeyValues(cardsList, 'category'));
    }, [cardsList]);

    const handleTabClick = (tabName: string, index: number) => {
        setOpen(false);
        setChecked((prev) => !prev);
        setTimeout(() => {
            setSelectedCat({ index, label: tabName });
        }, 180);
        setTimeout(() => {
            setChecked((prev) => !prev);
        }, 200);
    };

    const renderCardContent = (card: any, index: number) => {
        if (card.cardLibUI) {
            return (
                <CardImgCta
                    title={card.title}
                    description={richTextToPlainText(
                        JSON.parse(card.description.raw),
                    )}
                    ctaLabel={card.btnTitle}
                    ctaAction={card.redirect}
                    image={card.bg}
                    variant={card.cardLibUI}
                    category={card.label}
                    key={index}
                />
            );
        }
        if (card.isCard) {
            return (
                <div key={index} className={styles.o_cards_cats__nocard}>
                    {card.title && (
                        <div className={styles.o_cards_cats__nodesc}>
                            <i className="icon-Pagar-facturas-y-cre-fill" />
                            <div>
                                <Typography component="span">
                                    <b>{card.title.split(';')[0]}</b>
                                </Typography>
                                <span>{card.title.split(';')[1]}</span>
                            </div>
                        </div>
                    )}
                    {card.label && (
                        <p className={styles.o_cards_cats__nolabel}>
                            {card.label}
                        </p>
                    )}
                    {card.btnTitle && (
                        <Button2
                            label={card.btnTitle}
                            redirect={card.redirect || ''}
                            classname={styles.o_cards_cats__btn}
                            onClick={
                                !card.redirect
                                    ? (e: any) => {
                                        e.preventDefault();
                                        setOpenModal(!openModal);
                                    }
                                    : undefined
                            }
                        />
                    )}
                </div>
            );
        }

        return (
            <div
                key={index}
                className={styles.o_cards_cats__card}
                style={{
                    backgroundImage: `url(${card.bg})`,
                    opacity: checked ? 1 : 0.5,
                }}
            >
                <Typography
                    component="h3"
                    className={styles.o_cards_cats__title}
                >
                    {card.title}
                </Typography>
                <Typography
                    component="p"
                    className={styles.o_cards_cats__desc}
                >
                    <RichtextDescription descripcion={card.description} />
                </Typography>
                <a href={card.redirect} className={styles.o_cards_cats__link}>
                    {card.btnTitle}
                </a>
            </div>
        );
    };

    return (
        <Fade in={isVisible} timeout={1000}>
            <section
                ref={cardsCatRef}
                className={styles.o_cards_cats}
            >
                {bg && (
                    <img
                        className={styles.o_cards_cats__bg}
                        src={bg}
                        alt="Background"
                    />
                )}
                {subtitle && (
                    <Typography variant="body1" className="a-home__desc">
                        {subtitle}
                    </Typography>
                )}
                {title && (
                    <Typography component="h2" className="a-home__h2">
                        {title}
                    </Typography>
                )}
                {tabs.length > 1 &&
                    (screenSize > 968 ? (
                        <div className={styles.o_cards_cats__switcher}>
                            {tabs.map((tab, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleTabClick(tab, index)}
                                    className={`${selectedCat.label === tab
                                        ? styles.o_cards_cats__tab__active
                                        : ''
                                        } ${styles.o_cards_cats__tab}`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <DropdownMenu
                            open={open}
                            setOpen={setOpen}
                            selectedCat={selectedCat}
                            handleTabClick={handleTabClick}
                            tabs={tabs}
                        />
                    ))}
                <Grow in={checked} timeout={500}>
                    <div id="o_cards_cats__cards--effect">
                        <div
                            className={
                                btnLabel
                                    ? styles.o_cards_cats__container_cards
                                    : styles.o_cards_cats__cards
                            }
                        >
                            {groupedData[selectedCat.label]?.map(
                                (card: any, index: number) =>
                                    renderCardContent(card, index),
                            )}
                        </div>
                        {btnLabel && (
                            <Button2
                                label={btnLabel}
                                redirect={btnAction || ''}
                                classname={styles.o_cards_cats__btn__center}
                            />
                        )}
                    </div>
                </Grow >
                {groupedData &&
                    groupedData[selectedCat.label] &&
                    groupedData[selectedCat.label].length > 0 && (
                        <Modal
                            open={openModal}
                            onClose={() => setOpenModal(false)}
                            className={styles.o_cards_cats__modal}
                        >
                            <ModalForCard
                                data={separarPorHR(
                                    documentToReactComponents(
                                        JSON.parse(
                                            groupedData[
                                                selectedCat.label
                                            ].slice(-1)[0].description.raw,
                                        ),
                                    ),
                                )}
                            >
                                <Button2
                                    label="Cerrar"
                                    classname="m_modalforcard__modalbtn"
                                    onClick={() => setOpenModal(false)}
                                />
                            </ModalForCard>
                        </Modal>
                    )}
            </section >
        </Fade >
    );
};

export default CardsCategorias;
